import { GetStaticProps } from 'next';
import React from 'react';

import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetThemeOverview } from 'api/graphql-helper';
import {
	Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
	Enum_Componentinternaldefaultmoduleoptions_Padding,
	PageInformationFieldsFragment,
	ThemeOverviewSingleFieldsFragment
} from 'api/graphql-types';

import { Head } from 'modules/head/head';
import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import IntroHeader from 'modules/intro-header/intro-header';
import { BaseModule } from 'modules/base-module/base-module';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { initialState as settingsInitialState } from 'redux/stores/settings';
import { IBasePageProps } from 'types/page';
import { ROUTES } from 'constants/routes';

interface IThemeOverviewPageProps extends IBasePageProps {
	themeOverview?: ThemeOverviewSingleFieldsFragment;
	pageInformation?: PageInformationFieldsFragment;
}

const ThemeOverviewPage = (props: IThemeOverviewPageProps): React.JSX.Element => {
	const modules = props.themeOverview?.modules;
	const seoModule = props?.themeOverview?.seo;

	const headerDefaultOptions = {
		backgroundColor: props?.themeOverview?.header
			?.backgroundColor as Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
		padding: 'none' as Enum_Componentinternaldefaultmoduleoptions_Padding
	};

	return (
		<Layout navbar={props?.pageInformation?.navbar} footer={props?.pageInformation?.footer}>
			<Head seoModule={seoModule} seoParent={props?.pageInformation?.seo} />
			<BaseModule defaultModuleOptions={headerDefaultOptions}>
				<IntroHeader module={props?.themeOverview?.header} slug={ROUTES.THEMES.fullPath} />
			</BaseModule>

			<ModuleBuilder modules={modules} slug={ROUTES.THEMES.fullPath} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetThemeOverview(
			{
				variables: {
					locale,
					publicationState
				}
			},
			client
		);
		const { themeOverview, pageInformation } = result.props.data;

		if (themeOverview == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				pageInformation: pageInformation?.data?.attributes,
				themeOverview: themeOverview.data?.attributes,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'theme-overview',
						contentTypeId: themeOverview?.data?.documentId
					}
				}
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in theme overview', error);

		return {
			notFound: true
		};
	}
};

export default ThemeOverviewPage;
